import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Calculator, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (n === null || n === undefined ? "—" : `R ${Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(2)}%`);

export default function MarkupCoverage() {
  const now = new Date();
  const [periodMode, setPeriodMode] = useState("trailing"); // 'trailing' | 'specific_month'
  const [months, setMonths] = useState(12);
  const [specYear, setSpecYear] = useState(now.getFullYear());
  const [specMonth, setSpecMonth] = useState(now.getMonth() + 1);
  const [targetMode, setTargetMode] = useState("rands"); // 'rands' or 'pct'
  const [targetRands, setTargetRands] = useState(600000);
  const [targetPct, setTargetPct] = useState(7);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Sales-basis (only relevant in specific_month mode):
  //   • mtd       — raw to-date numbers (honest only for completed months)
  //   • projected — extrapolate partial month to full month via open business days
  //   • manual    — type your own monthly net-sales / COGS estimate
  const [salesBasis, setSalesBasis] = useState("projected");
  const [manualNetSales, setManualNetSales] = useState("");
  const [manualCogs, setManualCogs] = useState("");

  // What-if simulator state (markup-on-cost %)
  const [whatIfMarkup, setWhatIfMarkup] = useState(50);
  // Snap to required-markup once data loads (one-time)
  const [hasSyncedRequired, setHasSyncedRequired] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (periodMode === "specific_month") {
        params.set("year", specYear);
        params.set("month", specMonth);
        params.set("months", 1);  // ignored by backend in this mode, but harmless
        // Past / future / current month resolution → effective sales basis
        const isCurrent = (Number(specYear) === now.getFullYear() && Number(specMonth) === now.getMonth() + 1);
        const isFuture = (Number(specYear) > now.getFullYear()) ||
                         (Number(specYear) === now.getFullYear() && Number(specMonth) > now.getMonth() + 1);
        let effectiveBasis = salesBasis;
        if (!isCurrent && !isFuture && salesBasis === "projected") effectiveBasis = "mtd";  // past month
        if (isFuture && salesBasis !== "manual") effectiveBasis = "manual";                    // future month
        params.set("sales_basis", effectiveBasis);
        if (effectiveBasis === "manual") {
          if (manualNetSales !== "") params.set("manual_net_sales", manualNetSales);
          if (manualCogs !== "") params.set("manual_cogs", manualCogs);
        }
      } else {
        params.set("months", months);
      }
      if (targetMode === "rands") {
        params.set("target_net_profit", targetRands || 0);
      } else {
        params.set("target_net_profit_pct", targetPct || 0);
      }
      const res = await axios.get(`${API}/markup-coverage/analyze?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load markup analysis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = data?.totals;
  const tg = data?.target;
  const gapShortfall = tg?.gap_rands > 0;

  // Initialise the slider to the required markup the first time data arrives,
  // so the user immediately sees a "what-if" anchored at the target.
  useEffect(() => {
    if (data && !hasSyncedRequired && tg?.required_markup_pct) {
      const r = Math.min(150, Math.max(0, Math.round(tg.required_markup_pct)));
      if (r > 0) setWhatIfMarkup(r);
      setHasSyncedRequired(true);
    }
  }, [data, tg, hasSyncedRequired]);

  // ---------- What-if math ----------
  // Holds COGS constant (i.e. same units sold at same cost prices). Higher
  // markup -> higher net sales, higher gross profit, higher net profit.
  // Assumes OPEX stays flat. Break-even net sales = OPEX × (100+markup)/markup.
  const whatIf = (() => {
    if (!t || !t.cogs || t.cogs <= 0) return null;
    const m = Math.max(0, whatIfMarkup);
    const cogs = t.cogs;
    const opex = t.opex_total || 0;
    const projNetSales = cogs * (1 + m / 100);
    const projGP = cogs * (m / 100);
    const projNP = projGP - opex;
    const projMargin = m > 0 ? (m / (100 + m)) * 100 : 0;
    const breakEvenSales = m > 0 ? opex * (100 + m) / m : 0;
    const breakEvenCogs = m > 0 ? opex * 100 / m : 0;
    const npDelta = projNP - (t.actual_net_profit || 0);
    return {
      projNetSales,
      projGP,
      projNP,
      projMargin,
      breakEvenSales,
      breakEvenCogs,
      npDelta,
    };
  })();

  return (
    <div className="space-y-6 p-4 max-w-7xl mx-auto" data-testid="markup-coverage-page">
      <div className="flex items-center gap-3">
        <Calculator className="h-7 w-7 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold">Markup Coverage Calculator</h1>
          <p className="text-sm text-gray-600">Work out the markup % you need so sales cover overheads + your target net profit.</p>
        </div>
      </div>

      {/* Inputs */}
      <Card>
        <CardHeader><CardTitle className="text-base">Inputs</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-xs">Period</Label>
              <select
                className="border rounded px-2 text-sm h-9 w-full"
                value={periodMode}
                onChange={(e) => setPeriodMode(e.target.value)}
                data-testid="period-mode-select"
              >
                <option value="trailing">Trailing window</option>
                <option value="specific_month">Specific month</option>
              </select>
            </div>
            {periodMode === "trailing" ? (
              <div>
                <Label className="text-xs">Trailing window (months)</Label>
                <Input
                  type="number" min={1} max={36}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value) || 12)}
                  data-testid="months-input"
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs">Year / Month</Label>
                <div className="flex gap-2">
                  <Input
                    type="number" min={2020} max={2100}
                    value={specYear}
                    onChange={(e) => setSpecYear(Number(e.target.value) || now.getFullYear())}
                    data-testid="spec-year-input"
                  />
                  <select
                    className="border rounded px-2 text-sm h-9"
                    value={specMonth}
                    onChange={(e) => setSpecMonth(Number(e.target.value))}
                    data-testid="spec-month-select"
                  >
                    {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((mn, i) => (
                      <option key={i+1} value={i+1}>{mn}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Target net profit</Label>
              <div className="flex gap-2">
                <select
                  className="border rounded px-2 text-sm"
                  value={targetMode}
                  onChange={(e) => setTargetMode(e.target.value)}
                  data-testid="target-mode-select"
                >
                  <option value="rands">Rands</option>
                  <option value="pct">% of sales</option>
                </select>
                {targetMode === "rands" ? (
                  <Input
                    type="number" min={0}
                    value={targetRands}
                    onChange={(e) => setTargetRands(Number(e.target.value) || 0)}
                    data-testid="target-rands-input"
                    placeholder={periodMode === "specific_month" ? "e.g. 20000 (for 1 month)" : "e.g. 240000 (for 12m)"}
                  />
                ) : (
                  <Input
                    type="number" min={0} max={100} step="0.1"
                    value={targetPct}
                    onChange={(e) => setTargetPct(Number(e.target.value) || 0)}
                    data-testid="target-pct-input"
                    placeholder="e.g. 7 (%)"
                  />
                )}
              </div>
            </div>
            <Button onClick={fetchData} disabled={loading} data-testid="recalculate-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
              Recalculate
            </Button>
          </div>

          {/* Sales-basis controls — only meaningful for Specific month */}
          {periodMode === "specific_month" && (() => {
            const isCurrent = (Number(specYear) === now.getFullYear() && Number(specMonth) === now.getMonth() + 1);
            const isFuture = (Number(specYear) > now.getFullYear()) ||
                             (Number(specYear) === now.getFullYear() && Number(specMonth) > now.getMonth() + 1);
            const isPast = !isCurrent && !isFuture;
            // Effective basis (mirrors the resolution in fetchData) so the UI matches what's sent.
            let effective = salesBasis;
            if (isPast && salesBasis === "projected") effective = "mtd";
            if (isFuture && salesBasis !== "manual") effective = "manual";
            return (
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sales-basis-block">
                <div>
                  <Label className="text-xs">Sales basis</Label>
                  <select
                    className="w-full border rounded px-2 text-sm h-9 mt-1"
                    value={effective}
                    onChange={(e) => setSalesBasis(e.target.value)}
                    data-testid="sales-basis-select"
                  >
                    <option value="mtd">Month-to-date actual</option>
                    {!isPast && <option value="projected">Project to full month</option>}
                    <option value="manual">Manual estimate</option>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                    {isCurrent && effective === "projected" && "Extrapolates partial-month actuals to the full month using elapsed business days."}
                    {isCurrent && effective === "mtd" && "Compares MTD actual sales vs FULL-month OPEX — only honest for completed months."}
                    {isCurrent && effective === "manual" && "Uses the numbers you type below — handy if you know this month will be unusual."}
                    {isFuture && "Future month — type your own monthly sales / COGS estimate below."}
                    {isPast && "Completed month — actual numbers used as-is."}
                  </p>
                </div>
                {effective === "manual" && (
                  <>
                    <div>
                      <Label className="text-xs">Manual Net Sales (R, excl VAT)</Label>
                      <Input
                        type="number" min={0} step="0.01"
                        value={manualNetSales}
                        onChange={(e) => setManualNetSales(e.target.value)}
                        placeholder="e.g. 500000"
                        className="mt-1"
                        data-testid="manual-net-sales-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Manual COGS (R, excl VAT)</Label>
                      <Input
                        type="number" min={0} step="0.01"
                        value={manualCogs}
                        onChange={(e) => setManualCogs(e.target.value)}
                        placeholder="e.g. 420000"
                        className="mt-1"
                        data-testid="manual-cogs-input"
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Headline numbers */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">
                {data.period.label || `Trailing ${data.period.months}m`} Net Sales (excl VAT)
                {data.period.sales_basis === "projected" && (
                  <span className="ml-1 inline-block text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded align-middle" data-testid="projected-badge">Projected</span>
                )}
                {data.period.sales_basis === "manual" && (
                  <span className="ml-1 inline-block text-[10px] font-bold uppercase bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded align-middle" data-testid="manual-badge">Manual</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{fmt(t.net_sales_excl_vat)}</div>
              <div className="text-xs text-gray-500">COGS: {fmt(t.cogs)}</div>
              {data.period.sales_basis === "projected" && data.period.elapsed_business_days > 0 && (
                <div className="text-[11px] text-amber-700 mt-1 italic">
                  Projected from {data.period.elapsed_business_days} of {data.period.total_business_days} business days
                  (×{data.period.projection_factor.toFixed(2)})
                </div>
              )}
              {data.period.sales_basis === "manual" && (
                <div className="text-[11px] text-purple-700 mt-1 italic">
                  Using your manual estimate · catalogue mix scaled proportionally
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-600">Actual GP & Margin</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{fmt(t.actual_gross_profit)}</div>
              <div className="text-xs text-gray-500">Margin {pct(t.actual_margin_pct)} · Markup {pct(t.actual_markup_pct)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-600">OPEX (this window)</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{fmt(t.opex_total)}</div>
              <div className="text-xs text-gray-500">Net Profit (actual): <span className={t.actual_net_profit < 0 ? "text-red-600 font-semibold" : "text-green-700 font-semibold"}>{fmt(t.actual_net_profit)}</span></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Verdict */}
      {data && (
        <Card className={gapShortfall ? "border-amber-300 bg-amber-50/50" : "border-green-300 bg-green-50/40"}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              {gapShortfall ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-green-700" />}
              {gapShortfall ? "Required margin not yet met" : "On track — current margin covers your target"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-600">Target Net Profit</div>
                <div className="text-lg font-bold">{fmt(tg.target_net_profit)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Required Gross Profit</div>
                <div className="text-lg font-bold">{fmt(tg.required_gross_profit)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Required Blended Margin</div>
                <div className="text-lg font-bold text-indigo-700">{pct(tg.required_margin_pct)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Required Markup on Cost</div>
                <div className="text-lg font-bold text-indigo-700">{pct(tg.required_markup_pct)}</div>
              </div>
            </div>
            <div className="mt-3 text-sm">
              {gapShortfall ? (
                <>You're <strong className="text-amber-700">{fmt(tg.gap_rands)}</strong> short of the gross profit needed to cover OPEX + your target net profit. Raise blended margin from <strong>{pct(t.actual_margin_pct)}</strong> to <strong>{pct(tg.required_margin_pct)}</strong>.</>
              ) : (
                <>Your current margin of <strong>{pct(t.actual_margin_pct)}</strong> already exceeds the required <strong>{pct(tg.required_margin_pct)}</strong>. Surplus: <strong className="text-green-700">{fmt(Math.abs(tg.gap_rands))}</strong>.</>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* What-if slider */}
      {data && whatIf && (
        <Card data-testid="whatif-card" className="border-indigo-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-600" />
              What-if: drag the markup slider
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Blended markup on cost</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number" min={0} max={300} step={1}
                      value={whatIfMarkup}
                      onChange={(e) => setWhatIfMarkup(Math.max(0, Math.min(300, Number(e.target.value) || 0)))}
                      className="w-24 h-8"
                      data-testid="whatif-input"
                    />
                    <span className="text-sm font-semibold text-indigo-700 w-16">{whatIfMarkup}%</span>
                  </div>
                </div>
                <Slider
                  value={[whatIfMarkup]}
                  min={0}
                  max={150}
                  step={1}
                  onValueChange={(v) => setWhatIfMarkup(v[0])}
                  data-testid="whatif-slider"
                />
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                  <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span><span>125%</span><span>150%</span>
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  ≈ <strong>{whatIf.projMargin.toFixed(2)}%</strong> margin
                  {" · "}
                  Required to hit target: <strong>{pct(tg.required_markup_pct)}</strong>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="p-3 bg-gray-50 rounded">
                  <div className="text-xs text-gray-600">Projected Net Sales</div>
                  <div className="text-lg font-bold">{fmt(whatIf.projNetSales)}</div>
                  <div className="text-[10px] text-gray-500">vs actual {fmt(t.net_sales_excl_vat)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <div className="text-xs text-gray-600">Projected Gross Profit</div>
                  <div className="text-lg font-bold text-indigo-700">{fmt(whatIf.projGP)}</div>
                  <div className="text-[10px] text-gray-500">vs actual {fmt(t.actual_gross_profit)}</div>
                </div>
                <div className={`p-3 rounded ${whatIf.projNP >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                  <div className="text-xs text-gray-600">Projected Net Profit</div>
                  <div className={`text-lg font-bold ${whatIf.projNP >= 0 ? "text-green-700" : "text-red-700"}`}>{fmt(whatIf.projNP)}</div>
                  <div className="text-[10px] text-gray-500">
                    Δ vs current: <strong className={whatIf.npDelta >= 0 ? "text-green-700" : "text-red-700"}>
                      {whatIf.npDelta >= 0 ? "+" : ""}{fmt(whatIf.npDelta)}
                    </strong>
                  </div>
                </div>
                <div className="p-3 bg-amber-50/60 rounded">
                  <div className="text-xs text-gray-600">Break-even Net Sales</div>
                  <div className="text-lg font-bold text-amber-700">{whatIfMarkup > 0 ? fmt(whatIf.breakEvenSales) : "—"}</div>
                  <div className="text-[10px] text-gray-500">at this markup, you cover OPEX exactly</div>
                </div>
              </div>

              <div className="text-xs text-gray-600 bg-blue-50/40 border border-blue-100 rounded p-2">
                <strong>Assumptions</strong>: cost of goods stays the same (you sell the same units at the same costs), OPEX stays flat at {fmt(t.opex_total)} for the {data.period.label || `${data.period.months}-month`} window, and the markup is applied <em>blended</em> across the catalogue. Real-world demand may drop if you push prices too high — treat this as a sensitivity check, not a guarantee.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs: category breakdown, opex, reference */}
      {data && (
        <Tabs defaultValue="categories" className="w-full">
          <TabsList>
            <TabsTrigger value="categories" data-testid="tab-categories">By Category</TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers">By Supplier</TabsTrigger>
            <TabsTrigger value="opex" data-testid="tab-opex">OPEX Breakdown</TabsTrigger>
            <TabsTrigger value="reference" data-testid="tab-reference">Margin ↔ Markup</TabsTrigger>
          </TabsList>

          <TabsContent value="categories">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Per-category margin (sorted by sales share)</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Net Sales</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                      <TableHead className="text-right">Gross Profit</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                      <TableHead className="text-right">Markup</TableHead>
                      <TableHead className="text-right">vs Required</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.categories || []).map((c, i) => {
                      const below = c.gap_vs_required_pp < 0;
                      return (
                        <TableRow key={i} data-testid={`cat-row-${i}`} className={below ? "bg-red-50/50" : ""}>
                          <TableCell className="font-medium">{c.category}</TableCell>
                          <TableCell className="text-right">{fmt(c.net_sales_excl_vat)}</TableCell>
                          <TableCell className="text-right">{pct(c.sales_share_pct)}</TableCell>
                          <TableCell className="text-right">{fmt(c.gross_profit)}</TableCell>
                          <TableCell className="text-right">{pct(c.margin_pct)}</TableCell>
                          <TableCell className="text-right">{pct(c.markup_pct)}</TableCell>
                          <TableCell className={`text-right font-semibold ${below ? "text-red-600" : "text-green-700"}`}>
                            {below ? "" : "+"}{c.gap_vs_required_pp} pp
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="mt-2 text-xs text-gray-500">Red rows are categories pulling your blended margin down. Lift them first.</div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="suppliers">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Per-supplier margin (sorted by sales share)</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Net Sales</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                      <TableHead className="text-right">Gross Profit</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                      <TableHead className="text-right">Markup</TableHead>
                      <TableHead className="text-right">vs Required</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.suppliers || []).map((s, i) => {
                      const below = s.gap_vs_required_pp < 0;
                      return (
                        <TableRow key={i} data-testid={`sup-row-${i}`} className={below ? "bg-red-50/50" : ""}>
                          <TableCell className="font-medium">{s.supplier}</TableCell>
                          <TableCell className="text-right">{fmt(s.net_sales_excl_vat)}</TableCell>
                          <TableCell className="text-right">{pct(s.sales_share_pct)}</TableCell>
                          <TableCell className="text-right">{fmt(s.gross_profit)}</TableCell>
                          <TableCell className="text-right">{pct(s.margin_pct)}</TableCell>
                          <TableCell className="text-right">{pct(s.markup_pct)}</TableCell>
                          <TableCell className={`text-right font-semibold ${below ? "text-red-600" : "text-green-700"}`}>
                            {below ? "" : "+"}{s.gap_vs_required_pp} pp
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="mt-2 text-xs text-gray-500">Red rows are suppliers whose product mix is below your required blended margin. These are your renegotiation targets — go talk to the supplier's rep about cost reductions, or raise the sell prices on their range.</div>
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="opex">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Operating expenses ({data.period.label || `${data.period.months}m`})</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.opex_breakdown || []).map((o, i) => (
                      <TableRow key={i} className={!o.included_in_opex ? "bg-gray-100" : ""}>
                        <TableCell className="font-medium">{o.category}</TableCell>
                        <TableCell className="text-right">{fmt(o.amount)}</TableCell>
                        <TableCell className="text-right">{o.count}</TableCell>
                        <TableCell className="text-xs text-gray-500">{o.included_in_opex ? "Included in OPEX" : "Informational (already in COGS)"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reference">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Margin ↔ Markup reference</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Margin %</TableHead>
                      <TableHead>Markup on Cost %</TableHead>
                      <TableHead>Example: cost R100 → sell at</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.reference_table || []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.margin_pct}%</TableCell>
                        <TableCell className="font-semibold">{r.markup_pct.toFixed(2)}%</TableCell>
                        <TableCell>R {(100 + r.markup_pct).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-3 text-xs text-gray-500 space-y-1">
                  {(data.notes || []).map((n, i) => <div key={i}>• {n}</div>)}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
